import { useState, useEffect, useRef } from 'react';
import { animate } from 'animejs';
import { PortableText } from '@portabletext/react';
import { urlFor } from '../sanity/client';
import ImageGallery from './ImageGallery';

const EventModal = ({ isOpen, onClose, event }) => {
    const modalRef = useRef(null);
    const backdropRef = useRef(null);
    const [showModal, setShowModal] = useState(false);

    // Handle open/close animations
    useEffect(() => {
        if (isOpen) {
            setShowModal(true);
            // Animate In
            setTimeout(() => {
                animate(backdropRef.current, {
                    opacity: [0, 1],
                    duration: 500,
                    ease: 'out(2)'
                });
                animate(modalRef.current, {
                    opacity: [0, 1],
                    scale: [0.96, 1],
                    translateY: [10, 0],
                    duration: 400,
                    ease: 'out(3)'
                });
            }, 10);
        } else {
            // Animate Out
            if (showModal) {
                animate(backdropRef.current, {
                    opacity: 0,
                    duration: 400,
                    ease: 'in(2)'
                });
                animate(modalRef.current, {
                    opacity: 0,
                    scale: 0.95,
                    translateY: 20,
                    duration: 400,
                    ease: 'in(2)',
                    onComplete: () => setShowModal(false)
                });
            }
        }
    }, [isOpen, showModal]);

    // Debugging images
    useEffect(() => {
        if (event) {
            console.log('Event Data:', event);
            console.log('Main Image:', event.image);
            console.log('Gallery:', event.imageGallery);
        }
    }, [event]);

    // Handle ESC key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    // Lock body scroll when open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!showModal || !event) return null;

    // Helper to format date
    const getMonthYear = (monthIndex) => {
        const startYear = 2018;
        const year = startYear + Math.floor(monthIndex / 12);
        const month = monthIndex % 12;
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${monthNames[month]} ${year}`;
    };

    // Portable Text components
    const portableTextComponents = {
        block: {
            normal: ({ children }) => <p className="mb-4 text-[#959595] leading-relaxed">{children}</p>,
            h1: ({ children }) => <h1 className="text-white text-2xl font-bold mb-4">{children}</h1>,
            h2: ({ children }) => <h2 className="text-white text-xl font-bold mb-3 mt-6">{children}</h2>,
            h3: ({ children }) => <h3 className="text-white text-lg font-bold mb-2 mt-4">{children}</h3>,
            blockquote: ({ children }) => <blockquote className="border-l-2 border-[#F5B50A] pl-4 italic my-4 text-white/80">{children}</blockquote>,
        },
        list: {
            bullet: ({ children }) => <ul className="list-disc pl-5 mb-4 text-[#959595] space-y-1">{children}</ul>,
            number: ({ children }) => <ol className="list-decimal pl-5 mb-4 text-[#959595] space-y-1">{children}</ol>,
        },
        marks: {
            strong: ({ children }) => <strong className="font-bold text-white">{children}</strong>,
            em: ({ children }) => <em className="italic">{children}</em>,
            link: ({ value, children }) => (
                <a href={value?.href} target="_blank" rel="noopener noreferrer" className="text-white underline hover:text-[#F5B50A] transition-colors">
                    {children}
                </a>
            ),
        },
    };

    // Prepare images array with optimized URLs
    // Prioritize gallery images; only use featured image if no gallery
    const allImages = [];
    if (event.imageGallery && Array.isArray(event.imageGallery) && event.imageGallery.length > 0) {
        allImages.push(...event.imageGallery.map(img => urlFor(img).width(1200).auto('format').url()));
    } else if (event.image) {
        // Fallback to featured image if no gallery exists
        allImages.push(urlFor(event.image).width(1200).auto('format').url());
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4 lg:p-8">
            {/* Backdrop */}
            <div
                ref={backdropRef}
                className="absolute inset-0 bg-black/80 backdrop-blur-md"
                onClick={onClose}
                aria-hidden="true"
                style={{ opacity: 0 }}
            />

            {/* Modal Container */}
            <div
                ref={modalRef}
                className="relative w-full max-w-[1400px] h-full sm:h-[90vh] bg-[#050505] shadow-2xl overflow-hidden flex flex-col lg:flex-row border border-white/10"
                role="dialog"
                aria-modal="true"
                style={{ opacity: 0 }}
            >

                {/* Close Button (Absolute Top Right) - Minimalist */}
                <button
                    onClick={onClose}
                    className="absolute top-6 right-6 z-50 p-2 text-white/50 hover:text-white transition-colors"
                    aria-label="Close modal"
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>

                {/* LEFT COLUMN: Visuals (Sticky/Fixed on Desktop) */}
                <div className="w-full lg:w-[40%] h-[40vh] lg:h-full relative bg-black/20 border-b lg:border-b-0 lg:border-r border-white/10">
                    {allImages.length > 0 ? (
                        <div className="w-full h-full">
                            <ImageGallery
                                images={allImages}
                                title={event.title}
                                className="h-full aspect-auto"
                            />
                        </div>
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/20 text-sm">
                            [NO VISUALS AVAILABLE]
                        </div>
                    )}
                </div>

                {/* RIGHT COLUMN: Data (Scrollable) */}
                <div className="w-full lg:w-[60%] h-full overflow-y-auto bg-[#0A0A0A]">
                    <div className="p-6 lg:p-10 space-y-8">

                        {/* Header Section */}
                        <div>
                            <div className="flex items-center gap-3 mb-2">
                                <span className="px-2 py-0.5 bg-white/10 text-white/80 text-xs uppercase tracking-wider">
                                    {event.type}
                                </span>
                                <span className="text-[#666] text-sm">
                                    {getMonthYear(event.monthIndex)}
                                </span>
                            </div>
                            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-2 tracking-tight">
                                {event.title}
                            </h2>
                        </div>

                        {/* Tech Stack - Raw Text List */}
                        {event.techStack && event.techStack.length > 0 && (
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#666]">
                                {event.techStack.map((tech, i) => (
                                    <span key={i} className="flex items-center gap-2">
                                        <span className="w-1 h-1 bg-[#333]"></span>
                                        {tech}
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Description */}
                        {event.detailedDescription && (
                            <div className="prose prose-invert max-w-none">
                                <PortableText value={event.detailedDescription} components={portableTextComponents} />
                            </div>
                        )}

                        {/* Outcomes */}
                        {event.outcomes && event.outcomes.length > 0 && (
                            <div>
                                <h3 className="text-xs text-[#666] uppercase tracking-widest mb-4 border-b border-white/5 pb-2">
                                    {/* Key Outcomes */}
                                    Key Outcomes
                                </h3>
                                <ul className="space-y-3">
                                    {event.outcomes.map((outcome, i) => (
                                        <li key={i} className="flex gap-4 text-[#CCCCCC] text-sm leading-relaxed group">
                                            <span className="text-[#F5B50A]/50 group-hover:text-[#F5B50A] transition-colors">
                                                {String(i + 1).padStart(2, '0')}
                                            </span>
                                            <span>{outcome}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Footer Actions - Terminal Style */}
                        {event.links && event.links.length > 0 && (
                            <div className="pt-8 mt-8 border-t border-white/10 space-y-2">
                                {event.links.map((link, i) => (
                                    <a
                                        key={i}
                                        href={link.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block text-sm text-[#F5B50A] hover:text-white transition-colors group"
                                    >
                                        <span className="opacity-50 mr-2">&gt;</span>
                                        <span className="underline decoration-transparent group-hover:decoration-current underline-offset-4 transition-all">
                                            {link.label.toUpperCase().replace(/ /g, '_')}
                                        </span>
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EventModal;
